import React from 'react';
import { Layer, Source } from 'react-map-gl';
import { DIVISION_BOUNDRIES } from '../../../../../appConstants';
import { createSelector } from 'reselect';

interface IMapboxDepartmentLayerProps {
    polygonId : string;
    strokeId : string;
    labelId : string;

    polygonBeforeId ?: string;
    strokeBeforeId ?: string;
    labelBeforeId ?: string;

    minZoom ?: number;
    maxZoom ?: number;

    divisionGuid ?: string | null;
    subDivisionGuid ?: string | null;
    departmentGuid ?: string | null;

    visible ?: boolean;
}

interface IMapboxDepartmentLayerState {}

export default class MapboxDepartmentLayer extends React.PureComponent<IMapboxDepartmentLayerProps, IMapboxDepartmentLayerState> {
    constructor(props : IMapboxDepartmentLayerProps) {
        super(props);
        this.state = {};
    }

    private readonly getDivisionGuid = (props : IMapboxDepartmentLayerProps) => props.divisionGuid;
    private readonly getSubDivisionGuid = (props : IMapboxDepartmentLayerProps) => props.subDivisionGuid;
    private readonly getDepartmentGuid = (props : IMapboxDepartmentLayerProps) => props.departmentGuid;

    private getFilter = createSelector(
        [
            this.getDivisionGuid,
            this.getSubDivisionGuid,
            this.getDepartmentGuid,
        ],
        (
            divisionGuid,
            subDivisionGuid,
            departmentGuid,
        ) => {
            const result : Array<unknown> = [];

            if (divisionGuid) {
                result.push(['==', ['literal', divisionGuid], ['get', 'dptDivisionGuid']]);
            }

            if (subDivisionGuid) {
                result.push(['==', ['literal', subDivisionGuid], ['get', 'dptSubDivisionGuid']]);
            }

            if (departmentGuid) {
                result.push(['==', ['literal', departmentGuid], ['get', 'dptGuid']]);
            }
            
            return [
                'all',
                ...result,
            ];
        },
    );

    public readonly render = () => {
        const {
            minZoom = 0,
            maxZoom = 20,
            labelId,
            strokeId,
            polygonId,
            labelBeforeId,
            polygonBeforeId,
            strokeBeforeId,
            visible = true,
        } = this.props;

        const filter = this.getFilter(this.props);

        return (
            <Source
                id='polygon_department_source'
                type='vector'
                tiles={[
                    `${GEO_SERVER_URL}/geoserver/${GEO_SERVER_WORKSPACE}/gwc/service/wmts?REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&LAYER=farm_departments&STYLE=&TILEMATRIX=EPSG:900913:{z}&TILEMATRIXSET=EPSG:900913&FORMAT=application/vnd.mapbox-vector-tile&TILECOL={x}&TILEROW={y}&TILED=false`,
                ]}
                minzoom={minZoom}
                maxzoom={maxZoom}
                bounds={DIVISION_BOUNDRIES}
            >
                <Layer {...{
                    id: polygonId,
                    'source-layer': 'farm_departments',
                    type: 'fill',
                    minzoom: minZoom,
                    maxzoom: maxZoom,
                    'layout': {
                        visibility: visible ? 'visible' : 'none',
                    },
                    'paint': {
                        'fill-color': ['get', 'dptColor'],
                        'fill-opacity': 0.2,
                    },
                    'road-label-simple': true,
                    filter,
                    beforeId: polygonBeforeId,
                }}/>
                <Layer {...{
                    id: strokeId,
                    'source-layer': 'farm_departments',
                    'type': 'line',
                    minzoom: minZoom,
                    maxzoom: maxZoom,
                    'layout': {
                        visibility: visible ? 'visible' : 'none',
                    },
                    'paint': {
                        'line-color': ['get', 'dptColor'],
                        'line-width': 3,
                    },
                    filter,
                    beforeId: strokeBeforeId,
                }}/>
                <Layer {...{
                    id: labelId,
                    'source-layer': 'farm_departments',
                    type: 'symbol',
                    minzoom: minZoom,
                    maxzoom: maxZoom,
                    layout: {
                        visibility: visible ? 'visible' : 'none',
                        'text-field': [
                            'format',
                            [
                                'get',
                                'dptName',
                            ],
                            {
                                'font-scale': 0.8,
                            },
                        ],
                        'text-justify': 'center',
                        'text-allow-overlap': true,
                    },
                    paint: {
                        'text-opacity': 1,
                        'text-color': '#FFFFFF',
                        'text-halo-color': '#000000',
                        'text-halo-width': 2,
                        'text-halo-blur': 1,
                    },
                    filter,
                    beforeId: labelBeforeId,
                }}
                />
            </Source>
        );
    };
}
